import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@repo/ui/primitives/dialog";
import { Button } from "@repo/ui/primitives/button";
import { ScrollArea } from "@repo/ui/primitives/scroll-area";
import { Switch } from "@repo/ui/primitives/switch";
import { Label } from "@repo/ui/primitives/label";
import { Input } from "@repo/ui/primitives/input";
import { Textarea } from "@repo/ui/primitives/textarea";
import { Badge } from "@repo/ui/primitives/badge";
import { StatusBadge } from "@repo/ui/primitives/status-badge";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@repo/ui/primitives/avatar";
import {
  Settings,
  LayoutPanelLeft,
  User,
  Bell,
  Shield,
  Key,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  AtSign,
  UserCheck,
  Bug,
  Dock as DockGlyph,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useSidebarPreferencesStore } from "@/store/sidebar-preferences.store";
import { useSidebar } from "@repo/ui/primitives/sidebar";
import { useAuthStore } from "@/store/auth.store";
import { useNavigate } from 'react-router-dom';
import {
  ACCESS_LAB_AVAILABLE,
  readAccessLabEnabled,
  writeAccessLabEnabled,
} from "@/components/dev/access-lab/access-lab-preferences.js";
import { SecuritySettingsPanel } from "@/components/common/SecuritySettingsPanel.jsx";
import { DockSettingsPanel } from "@/components/common/DockSettingsPanel.jsx";
import {
  resolveDockPreferences,
  useDockPreferencesStore,
} from "@/store/dock-preferences.store.js";

const PreviewField = ({ label, value, icon: Icon }) => (
  <div className="flex-1 space-y-1.5">
    <Label className="ml-1 text-xs font-medium text-muted-foreground">
      {label}
    </Label>
    <div className="group flex items-center gap-3 rounded-[var(--ds-radius-control)] border border-border bg-muted/40 px-3.5 py-2.5 transition-colors hover:bg-muted/60 motion-reduce:transition-none">
      {Icon && (
        <div className="rounded-[var(--ds-radius-control-compact)] border border-border bg-card p-1 transition-colors group-hover:border-primary/40 motion-reduce:transition-none">
          <Icon className="h-3 w-3 text-muted-foreground transition-colors group-hover:text-primary motion-reduce:transition-none" />
        </div>
      )}
      <span className="text-sm font-medium text-foreground truncate">
        {value || "-"}
      </span>
    </div>
  </div>
);

const SettingsModal = ({ isOpen, onClose, dockPages = [], dockPreferenceKey = null }) => {
  const { t } = useTranslation("header");
  const sidebarT = useTranslation("sidebar").t;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState("sidebar");
  const { state, setOpen } = useSidebar();
  const { user } = useAuthStore();
  const storedDockPreferences = useDockPreferencesStore((store) =>
    dockPreferenceKey ? store.preferencesByScope[dockPreferenceKey] : undefined,
  );
  const setDockPreferences = useDockPreferencesStore((store) => store.setPreferences);
  const dockPreferences = React.useMemo(
    () => resolveDockPreferences(storedDockPreferences),
    [storedDockPreferences],
  );

  const {
    showIndicators,
    setShowIndicators,
    accordionMode,
    setAccordionMode,
    openOnHover,
    setOpenOnHover,
    density,
    setDensity,
  } = useSidebarPreferencesStore();

  // Local state for buffered saving
  const [localPrefs, setLocalPrefs] = React.useState({
    showIndicators,
    accordionMode,
    openOnHover,
    density,
    collapsed: state === "collapsed",
    accessLabEnabled: readAccessLabEnabled(),
    dock: dockPreferences,
  });

  // Reset local state when modal opens or store changes
  React.useEffect(() => {
    if (isOpen) {
      setLocalPrefs({
        showIndicators,
        accordionMode,
        openOnHover,
        density,
        collapsed: state === "collapsed",
        accessLabEnabled: readAccessLabEnabled(),
        dock: dockPreferences,
      });
    }
  }, [isOpen, showIndicators, accordionMode, openOnHover, density, state, dockPreferences]);

  const handleSave = () => {
    setShowIndicators(localPrefs.showIndicators);
    setAccordionMode(localPrefs.accordionMode);
    setOpenOnHover(localPrefs.openOnHover);
    setDensity(localPrefs.density);
    setOpen(!localPrefs.collapsed);
    writeAccessLabEnabled(localPrefs.accessLabEnabled);
    setDockPreferences(dockPreferenceKey, localPrefs.dock);
    onClose();
  };

  const tabs = [
    { id: "profile", label: t("settings.tabs.profile", { defaultValue: "Profile Settings" }), icon: User },
    { id: "sidebar", label: sidebarT("settings.title"), icon: LayoutPanelLeft },
    ...(dockPreferenceKey
      ? [{ id: "dock", label: t("settings.tabs.dock", { defaultValue: "Quick Access Dock" }), icon: DockGlyph }]
      : []),
    ...(ACCESS_LAB_AVAILABLE
      ? [{ id: "accessLab", label: t("settings.tabs.accessLab", { defaultValue: "Access Lab" }), icon: Bug }]
      : []),
    { id: "notifications", label: t("settings.tabs.notifications", { defaultValue: "Notifications" }), icon: Bell },
    { id: "security", label: t("settings.tabs.security", { defaultValue: "Security" }), icon: Shield },
  ];

  const activeTabData = tabs.find(t => t.id === activeTab);
  const ActiveIcon = activeTabData?.icon || Settings;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent showCloseButton={false} className="flex h-[85vh] max-w-[95vw] flex-col overflow-hidden bg-card p-0 sm:max-w-[70vw]">
        {/* Header - Matching UserForm style */}
        <div className="flex shrink-0 items-center justify-between border-b border-border-subtle px-6 py-4">
          <div>
            <DialogTitle className="flex items-center gap-3 text-xl font-medium text-foreground">
              <div className="flex size-10 items-center justify-center rounded-[var(--ds-radius-control)] bg-accent text-accent-foreground">
                <Settings className="w-5 h-5" />
              </div>
              {t("settingsLabel", { defaultValue: "Ayarlar" })}
            </DialogTitle>
            <DialogDescription className="mt-1">
              {t("settings.dialogDescription", { defaultValue: "You can manage system settings and preferences here." })}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              {t("form.buttons.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
            >
              {t("form.buttons.save", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar Tabs - Matching UserForm style */}
          <div className="flex w-64 shrink-0 flex-col border-r border-border-subtle bg-muted/30 py-4">
            <nav className="space-y-1 px-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
"flex w-full items-center gap-3 rounded-[var(--ds-radius-control)] px-3 py-2.5 text-left text-sm font-medium transition-colors motion-reduce:transition-none",
                      isActive
                        ? "bg-accent text-accent-foreground shadow-[var(--shadow-control)]"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className={cn("h-4 w-4", isActive ? "text-primary" : "text-muted-foreground")} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 overflow-hidden">
            <div className="p-4 lg:p-6 max-w-4xl mx-auto h-full">
              {activeTab === "sidebar" && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="mb-6 flex items-center gap-2 border-b border-border pb-3 text-lg font-medium text-foreground">
                      <LayoutPanelLeft className="h-5 w-5 text-primary" />
                      {sidebarT("settings.title")}
                    </h3>

                    <div className="grid grid-cols-1 gap-4">
                      <div className="flex flex-row items-center justify-between rounded-[var(--ds-radius-surface)] border border-border bg-muted/40 p-4">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-medium">{sidebarT("settings.showIndicators.label")}</Label>
                          <div className="text-[0.8rem] text-muted-foreground">
                            {sidebarT("settings.showIndicators.description")}
                          </div>
                        </div>
                        <Switch
                          checked={localPrefs.showIndicators}
                          onCheckedChange={(checked) => setLocalPrefs(prev => ({ ...prev, showIndicators: checked }))}
                          className="data-[state=checked]:bg-primary"
                        />
                      </div>

                      <div className="flex flex-row items-center justify-between rounded-[var(--ds-radius-control)] border border-border p-4">
                        <div className="space-y-0.5">
                          <Label className="text-base">{sidebarT("settings.accordionMode.label")}</Label>
                          <div className="text-[0.8rem] text-muted-foreground">
                            {sidebarT("settings.accordionMode.description")}
                          </div>
                        </div>
                        <Switch
                          checked={localPrefs.accordionMode}
                          onCheckedChange={(checked) => setLocalPrefs(prev => ({ ...prev, accordionMode: checked }))}
                        />
                      </div>

                      <div className="flex flex-row items-center justify-between rounded-[var(--ds-radius-control)] border border-border p-4">
                        <div className="space-y-0.5">
                          <Label className="text-base">{sidebarT("settings.openOnHover.label")}</Label>
                          <div className="text-[0.8rem] text-muted-foreground">
                            {sidebarT("settings.openOnHover.description")}
                          </div>
                        </div>
                        <Switch
                          checked={localPrefs.openOnHover}
                          onCheckedChange={(checked) => setLocalPrefs(prev => ({ ...prev, openOnHover: checked }))}
                        />
                      </div>

                      <div className="flex flex-row items-center justify-between rounded-[var(--ds-radius-control)] border border-border p-4">
                        <div className="space-y-0.5">
                          <Label className="text-base">{sidebarT("settings.density.label")}</Label>
                          <div className="text-[0.8rem] text-muted-foreground">
                            {sidebarT("settings.density.description")}
                          </div>
                        </div>
                        <Switch
                          checked={localPrefs.density === "compact"}
                          onCheckedChange={(checked) =>
                            setLocalPrefs((prev) => ({
                              ...prev,
                              density: checked ? "compact" : "comfortable",
                            }))
                          }
                        />
                      </div>

                      <div className="flex flex-row items-center justify-between rounded-[var(--ds-radius-control)] border border-border p-4">
                        <div className="space-y-0.5">
                          <Label className="text-base">{sidebarT("settings.collapsedMode.label")}</Label>
                          <div className="text-[0.8rem] text-muted-foreground">
                            {sidebarT("settings.collapsedMode.description")}
                          </div>
                        </div>
                        <Switch
                          checked={localPrefs.collapsed}
                          onCheckedChange={(collapsed) => setLocalPrefs(prev => ({ ...prev, collapsed }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "dock" && (
                <DockSettingsPanel
                  preferences={localPrefs.dock}
                  pages={dockPages}
                  onChange={(dock) => setLocalPrefs((current) => ({ ...current, dock }))}
                />
              )}

              {activeTab === "accessLab" && (
                <div className="space-y-6">
                  <h3 className="mb-6 flex items-center gap-2 border-b border-border pb-3 text-lg font-medium text-foreground">
                    <Bug className="h-5 w-5 text-warning" aria-hidden="true" />
                    {t("settings.accessLab.title", { defaultValue: "Access Lab" })}
                  </h3>

                  <div className="flex items-center justify-between gap-6 rounded-[var(--ds-radius-surface)] border border-border bg-muted/40 p-4">
                    <div className="space-y-1">
                      <Label htmlFor="access-lab-enabled" className="text-sm font-medium">
                        {t("settings.accessLab.toggleLabel", { defaultValue: "Show Access Lab" })}
                      </Label>
                      <p className="text-[0.8rem] text-muted-foreground">
                        {t("settings.accessLab.description", { defaultValue: "Turn the Access Lab panel on or off while inspecting roles and access permissions during development." })}
                      </p>
                    </div>
                    <Switch
                      id="access-lab-enabled"
                      checked={localPrefs.accessLabEnabled}
                      onCheckedChange={(accessLabEnabled) =>
                        setLocalPrefs((prev) => ({ ...prev, accessLabEnabled }))
                      }
                      className="shrink-0"
                    />
                  </div>
                </div>
              )}

              {activeTab === "profile" && (
                <div className="max-w-4xl mx-auto space-y-6">
                  {/* Profile Header - Compact Premium Look */}
                  <div className="group relative flex flex-col items-center gap-5 overflow-hidden rounded-[var(--ds-radius-workbench)] border border-border bg-card p-5 shadow-[var(--shadow-surface)] transition-shadow md:flex-row hover:shadow-[var(--shadow-surface-hover)] motion-reduce:transition-none">
                    <div className="relative">
                      <Avatar className="h-20 w-20 border-2 border-background shadow-[var(--shadow-surface)] ring-1 ring-border">
                        <AvatarImage src={user?.avatar} alt={user?.fullName} className="object-cover" />
                        <AvatarFallback className="bg-primary text-2xl font-bold text-primary-foreground">
                          {user?.firstName?.[0]}{user?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-background bg-success shadow-[var(--shadow-control)]">
                        <div className="h-1.5 w-1.5 rounded-full bg-background" />
                      </div>
                    </div>

                    <div className="flex-1 text-center md:text-left space-y-2 z-10">
                      <div className="space-y-0.5">
                        <h3 className="text-xl font-medium text-foreground">
                          {user?.fullName || `${user?.firstName} ${user?.lastName}`}
                        </h3>
                        <p className="text-xs text-muted-foreground font-medium flex items-center justify-center md:justify-start gap-1.5">
                          <AtSign className="h-3.5 w-3.5 text-primary" />
                          {user?.userName}
                        </p>
                      </div>

                      <div className="flex flex-wrap justify-center md:justify-start gap-2.5 pt-1">
                        <Badge variant="secondary" className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold">
                          <Shield className="w-3 h-3" />
                          {user?.role?.name || t("userFallbackRole", { defaultValue: "User" })}
                        </Badge>
                        <StatusBadge status={user?.status || "active"} className="flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold capitalize">
                          <div className={cn("h-1.5 w-1.5 rounded-full", user?.status === 'active' ? "bg-success" : "bg-muted-foreground")} />
                          {user?.status || "active"}
                        </StatusBadge>
                      </div>
                    </div>
                  </div>

                  {/* Profile Info Sections - Grid Preview Style */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 px-2 lg:px-4">
                    {/* Column 1: Personal Info */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2.5 border-b border-border-subtle pb-2.5 text-foreground">
                        <div className="rounded-[var(--ds-radius-control-compact)] bg-primary p-1.5 text-primary-foreground shadow-[var(--shadow-control)]">
                          <UserCheck className="h-3.5 w-3.5 text-primary-foreground" />
                        </div>
                        <h4 className="text-sm font-medium tracking-tight">
                          {t("settings.profile.personalInfo", { defaultValue: "Personal Information" })}
                        </h4>
                      </div>

                      <div className="space-y-4">
                        <PreviewField
                          label={t("settings.profile.firstName", { defaultValue: "Ad" })}
                          value={user?.firstName}
                          icon={User}
                        />
                        <PreviewField
                          label={t("settings.profile.lastName", { defaultValue: "Last name" })}
                          value={user?.lastName}
                          icon={User}
                        />
                        <PreviewField
                          label={t("settings.profile.userName", { defaultValue: "Username" })}
                          value={user?.userName}
                          icon={AtSign}
                        />
                      </div>
                    </div>

                    {/* Column 2: Contact Info */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2.5 border-b border-border-subtle pb-2.5 text-foreground">
                        <div className="rounded-[var(--ds-radius-control-compact)] bg-primary p-1.5 text-primary-foreground shadow-[var(--shadow-control)]">
                          <Mail className="h-3.5 w-3.5" />
                        </div>
                        <h4 className="text-sm font-medium tracking-tight">
                          {t("settings.profile.contactInfo", { defaultValue: "Contact Details" })}
                        </h4>
                      </div>

                      <div className="space-y-4">
                        <PreviewField
                          label={t("settings.profile.email", { defaultValue: "Email" })}
                          value={user?.email}
                          icon={Mail}
                        />
                        <PreviewField
                          label={t("settings.profile.phoneNumber", { defaultValue: "Phone" })}
                          value={user?.phoneNumber}
                          icon={Phone}
                        />
                        <PreviewField
                          label={t("settings.profile.title", { defaultValue: "Title" })}
                          value={user?.title}
                          icon={Briefcase}
                        />
                      </div>
                    </div>

                    {/* Address - Full Width */}
                    <div className="md:col-span-2 space-y-4 pt-2">
                      <div className="flex items-center gap-2.5 border-b border-border-subtle pb-2.5 text-foreground">
                        <div className="rounded-[var(--ds-radius-control-compact)] bg-primary p-1.5 text-primary-foreground shadow-[var(--shadow-control)]">
                          <MapPin className="h-3.5 w-3.5" />
                        </div>
                        <h4 className="text-sm font-medium tracking-tight">
                          {t("settings.profile.address", { defaultValue: "Address information" })}
                        </h4>
                      </div>
                      <div className="group flex min-h-[50px] items-start gap-3 rounded-[var(--ds-radius-control)] border border-border bg-muted/40 p-3 transition-colors hover:bg-muted/60 motion-reduce:transition-none">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span className="text-sm font-medium text-foreground leading-relaxed">
                          {user?.address || t("settings.profile.noAddress", { defaultValue: "No address information defined." })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "notifications" && (
                <div className="flex flex-col items-center justify-center space-y-4 py-20 text-center">
                  <div className="rounded-full bg-muted p-4">
                    <Bell className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <div className="max-w-md space-y-2">
                    <p className="text-xl font-semibold text-foreground">{activeTabData.label}</p>
                    <p className="text-sm text-muted-foreground">{t("settings.notifications.description", { defaultValue: "Choose your available notification channels and quiet hours." })}</p>
                  </div>
                  <Button type="button" onClick={() => { onClose(); navigate('/settings/notification-preferences'); }}>
                    {t("settings.notifications.open", { defaultValue: "Open notification preferences" })}
                  </Button>
                </div>
              )}

              {activeTab === "security" && (
                <ScrollArea className="h-full max-h-[calc(85vh-8rem)] pr-2">
                  <SecuritySettingsPanel />
                </ScrollArea>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
